/**
 * Determines if a URL points to a presigned S3 resource, blob, or data URI.
 * These URL types should NOT use crossOrigin="anonymous" because the CORS
 * preflight request can fail with S3 presigned URLs.
 */
export const isPresignedUrl = (url: string): boolean =>
  url.includes('amazonaws.com') || url.startsWith('blob:') || url.startsWith('data:');

/**
 * Determine if URL is a CloudFront CDN URL.
 * CloudFront URLs require crossOrigin="anonymous" for CORS.
 */
export const isCloudFrontUrl = (url: string): boolean => {
  return url.includes('.cloudfront.net') ||
         (url.startsWith('https://') && !url.includes('amazonaws.com'));
};

/**
 * Returns the appropriate crossOrigin attribute value for an image element.
 * - Presigned S3 URLs, blob URLs, and data URLs: undefined (no crossOrigin)
 * - CDN / external URLs: "anonymous"
 * - Null/undefined/empty: undefined (defensive guard)
 */
export const getCrossOrigin = (url: string | null | undefined): 'anonymous' | undefined => {
  if (!url) return undefined;
  return isPresignedUrl(url) ? undefined : 'anonymous';
};

/**
 * Generate a CDN-optimized URL by replacing the file extension and optionally
 * the size directory in the path.
 *
 * The query string is preserved untouched: CloudFront signed URLs (GH#1254)
 * carry their auth there (Policy/Signature/Key-Pair-Id), and the signature's
 * wildcard policy covers every size/format variant of the same image — so a
 * rewritten path with the original query stays valid.
 */
export const generateCdnUrl = (originalUrl: string, format: string, size?: string): string => {
  const queryIndex = originalUrl.indexOf('?');
  const path = queryIndex === -1 ? originalUrl : originalUrl.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : originalUrl.slice(queryIndex);

  const urlParts = path.split('/');
  const filename = urlParts[urlParts.length - 1];
  const [basename] = filename.split('.');

  const updatedParts = [
    ...urlParts.slice(0, urlParts.length - 1),
    `${basename}.${format}`,
  ];

  if (size) {
    const sizeIndex = updatedParts.findIndex(part =>
      ['thumb', 'small', 'medium', 'large'].includes(part),
    );
    if (sizeIndex !== -1) {
      return [
        ...updatedParts.slice(0, sizeIndex),
        size,
        ...updatedParts.slice(sizeIndex + 1),
      ].join('/') + query;
    }
  }

  return updatedParts.join('/') + query;
};

interface FormatSupport {
  avif: boolean;
  webp: boolean;
}

/**
 * Generate a srcSet string for responsive images across all size renditions.
 */
export const generateSrcSet = (baseUrl: string, formatSupport: FormatSupport): string => {
  const format = formatSupport.avif ? 'avif' : formatSupport.webp ? 'webp' : 'jpg';

  const sizes = [
    { size: 'thumb', width: 256 },
    { size: 'small', width: 512 },
    { size: 'medium', width: 1024 },
    { size: 'large', width: 2048 },
  ];

  return sizes
    .map(({ size, width }) => `${generateCdnUrl(baseUrl, format, size)} ${width}w`)
    .join(', ');
};

/**
 * Get a JPEG fallback URL for a CDN image URL.
 * Used during retry when the preferred format (avif/webp) fails.
 */
export const getCdnJpegFallbackUrl = (url: string): string => generateCdnUrl(url, 'jpeg');

// MIME types every modern browser decodes natively inside <img>.
const UNIVERSALLY_RENDERABLE_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

// MIME types only Safari decodes natively inside <img>.
const SAFARI_ONLY_MIMES = new Set(['image/heic', 'image/heif']);

// Lowercase extensions corresponding to UNIVERSALLY_RENDERABLE_MIMES.
const UNIVERSALLY_RENDERABLE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp']);

// Lowercase extensions corresponding to SAFARI_ONLY_MIMES.
const SAFARI_ONLY_EXTS = new Set(['heic', 'heif']);

const isSafari = (userAgent: string): boolean => {
  if (!userAgent) return false;
  const ua = userAgent.toLowerCase();
  // Safari UA contains "safari/" and lacks Chromium markers.
  return ua.includes('safari/') && !ua.includes('chrome') && !ua.includes('chromium') && !ua.includes('android');
};

const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
};

/**
 * Decide whether the browser can decode this file inside an `<img>` element.
 *
 * Used by the upload wizard to avoid handing a `blob:` URL pointing at a
 * non-renderable original (TIFF, RAW) to `<img>`, which would fail decode and
 * trigger the broken-image / "No Image Available" fallback. Non-renderable
 * files should display a "Processing…" affordance until the server-side
 * rendition lands.
 *
 * Default-deny: an unknown MIME with an unknown extension returns false.
 *
 * @param file - the File object to inspect
 * @param opts.userAgent - override `navigator.userAgent` (test seam)
 */
export const isBrowserRenderable = (
  file: File,
  opts?: { userAgent?: string },
): boolean => {
  const ua = opts?.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const mime = (file.type || '').toLowerCase();

  if (mime) {
    if (UNIVERSALLY_RENDERABLE_MIMES.has(mime)) return true;
    if (SAFARI_ONLY_MIMES.has(mime)) return isSafari(ua);
    // Authoritative MIME signal but not in either allowlist → not renderable.
    return false;
  }

  // Empty MIME: fall back to extension.
  const ext = extensionOf(file.name);
  if (!ext) return false;
  if (UNIVERSALLY_RENDERABLE_EXTS.has(ext)) return true;
  if (SAFARI_ONLY_EXTS.has(ext)) return isSafari(ua);
  return false;
};
